import { defineStore } from 'pinia'

export const useCacheStore = defineStore('cache', () => {
  const caches = ref<{ [key: string]: { status: 'fetching' | 'success' | 'error'; expire: number; value?: unknown } }>({})
  const inflight = new Map<string, Promise<unknown>>()

  /**
   * Check if data is still valid and populate using fetcher when not
   *
   * `key` must be a string that uniquely identifies this data
   * `fetcher` must be an async function that resolves into the data
   * `ttl` is an optional number that specifies the seconds before the data becomes stale
   */
  async function fetch<T>({ key, ttl, fetcher, fallback = undefined }: { key: string; ttl: number; fetcher: { (): Promise<T> }; fallback?: T }): Promise<T> {
    if (key in caches.value && ['success', 'error'].includes(caches.value[key].status) && new Date().getTime() <= caches.value[key].expire) {
      return caches.value[key].value as T
    }

    if (inflight.has(key)) {
      return inflight.get(key) as Promise<T>
    }

    const promise = (async () => {
      try {
        const value = await fetcher()
        caches.value[key] = { status: 'success', expire: new Date().getTime() + (ttl || 300) * 1000, value }
      } catch (error) {
        console.error('Error during "cache/fetch"', error)
        caches.value[key] = { status: 'error', expire: new Date().getTime() + (ttl || 300) * 500, value: fallback }
      } finally {
        inflight.delete(key)
      }
      return caches.value[key].value as T
    })()

    inflight.set(key, promise)
    caches.value[key] = { status: 'fetching', expire: 0 }

    return promise
  }

  return { caches, fetch }
})
